import { StoreState as RSS, getRootReducer } from './reducer'
import { compose, StoreEnhancer, createStore, Middleware, applyMiddleware, Store } from 'redux'
import createSagaMiddleware from 'redux-saga'
import { offline } from '@redux-offline/redux-offline'
import defaultOfflineConfig from '@redux-offline/redux-offline/lib/defaults'

import rootSaga from './sagas'
import { readyAction } from './actions'
import { setApiConfig } from '../api/index'

import { getConfiguration } from '../configuration/index'
import { DEFAULT_APP_INFO } from '../settings/reducer'
import { changeAppInfo, updateProcessingManifestState } from '../settings/actions'
import * as Sentry from '@sentry/react'

export type RootStoreState = RSS

/*  Create the redux-offline configuration, based on the default configuration. */
const offlineConfig = {
	...defaultOfflineConfig,

	/**
	 * This callback occurs after redux-persist has rehydrated our Redux state.
	 */
	persistCallback: () => {
		let { appInfo } = store.getState().settings
		const { appMetaData, selectedLanguage } = store.getState().settings

		// first check if appInfo is set on store, if not load from configuration and dispatch an action to store in on redux
		if (appInfo.appId === DEFAULT_APP_INFO.appId) {
			appInfo = getConfiguration().getDefaultAppInfo()
			store.dispatch(changeAppInfo({ id: appInfo.appId, projectId: appInfo.projectId, token: appInfo.appApiKey }))
		}

		// update the API with the persisted project id in case it was changed
		// had to do this early because we were checking for updates before the API instance had been updated to saved app id so was causing errors
		setApiConfig(appInfo.projectId, appInfo.appApiKey)

		// also had to set UI theme early too, as was when doing it via a saga that listened for the readyAction, the Home UI was rendered first, which requires the header style to be ready
		if (appMetaData) {
			getConfiguration().changeTheme(appMetaData)
		}

		// if user has selected language, update the static strings to use it
		if (selectedLanguage) {
			getConfiguration().changeLocalizationLanguage(selectedLanguage.code)
		}

		// blacklist the manifest property from being persisted to disk
		store.dispatch(updateProcessingManifestState({ event: 'clear' }))

		/* Let our app know that the application state has been rehydrated and is ready to be used. */
		store.dispatch(readyAction())
	},

	/**
	 * This function is used to handle actions tagged for redux-offline to handle.
	 */
	// effect: handleEffect,

	/**
	 * This function determines whether to discard a request, or to retry in, in the event
	 * of an error.
	 */
	// discard: handleDiscard,

	/* The commented out function below hard-codes the app to function as if it is offline. */
	// detectNetwork: function(callback: (online: boolean) => void) {
	// 	callback(false)
	// },

	persistOptions: {
		blacklist: [
			'collectionlist',
			'screen',
			'support',
			'ready',
			'settings',
			'audioplayer',
			'lockapp',
			'map',
			'scanner',
			'mediaitems',
			'forms',
			'badges',
		],
	},
}

export let store: Store<RootStoreState>

const sentryReduxEnhancer = Sentry.createReduxEnhancer({});

export function getStore() {
	if (store) {
		return store
	}

	/* Create the redux-saga middleware. */
	const sagaMiddleware = createSagaMiddleware()

	let middlewares: Middleware[] = [sagaMiddleware]
	middlewares = getConfiguration().customiseReduxMiddleware(middlewares)

	const reducer = getRootReducer() // platform reducer is already applied

	/* Enhancers for the store. */
	// @ts-ignore
	const enhancers = compose(
		/* Add the redux-offline store enhancer */
		offline(offlineConfig),

		/* Add the rmiddleware */
		applyMiddleware.apply(null, middlewares),

		/* Include the devtools. Comment this out if you don't want to use the dev tools. */
		// devToolsEnhancer({}),

		sentryReduxEnhancer,
	) as StoreEnhancer<RootStoreState>

	/* Create the store. We do not include an initial state, as each of the module reducers includes its own initial state. */
	store = createStore(reducer, enhancers)

	/* Run the root saga */
	sagaMiddleware.run(rootSaga)

	return store
}
